import * as React from 'react';

import { FormControlWithStatus } from 'components/Form/FormControlVerificationStatus';
import { IDOTMedicalCardFields } from 'common/document/ui/driver-document';
import { InputWithStatus } from 'components/Form/FormControlVerificationStatus/Input';
import { DatePickerWithStatus } from 'components/Form/FormControlVerificationStatus/DatePicker';
import { IFieldPolygon } from '../Utils/field-polygon';
import { IFieldVerificationResult } from '../Utils/verification-result';
import { shouldHideVerificationByStatus } from '../Utils/verification-status';
import { useFeatureFlags } from '../../../../contexts/featureFlag/useFeatureFlags';
import { Checkbox, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import styles from './styles.module.scss';
import FormControl from '@mui/material/FormControl';

type InputProps = {
  getFieldErrorProp: (field: keyof IDOTMedicalCardFields) => {
    error: string | undefined;
  };
  getFieldProps: (field: keyof IDOTMedicalCardFields) => {
    name: keyof IDOTMedicalCardFields;
    value: any;
    onChange: (inputValue: any) => void;
  };
  fieldVerified: { [key: string]: boolean };
  fieldPolygon: IFieldPolygon;
  verificationResult?: IFieldVerificationResult;
  verificationStatus?: any;
  onHoverField?: (polygon: any | null) => void;
  onUnHoverField?: () => void;
  onVerify?: () => void;
  disabled?: boolean;
  canApprove?: boolean;
  isReturnToWork?: boolean;
};

export const DOTMedicalCardForm = ({
  getFieldErrorProp,
  getFieldProps,
  fieldVerified,
  fieldPolygon,
  verificationResult,
  verificationStatus,
  onHoverField,
  onUnHoverField,
  onVerify,
  disabled = false,
  canApprove = true,
  isReturnToWork = false,
}: InputProps) => {
  const { featureFlags } = useFeatureFlags();

  const handleHoverField = (data: any) => {
    onHoverField && onHoverField(data);
  };

  const handleUnHoverField = () => {
    onUnHoverField && onUnHoverField();
  };

  return (
    <form>
      <FormControlWithStatus
        id="MedicalExaminersName"
        label="Medical Examiner’s Name"
        disabled={disabled}
      >
        <InputWithStatus
          {...getFieldProps('medicalExaminerName')}
          id="MedicalExaminersName"
          label="Medical Examiner’s Name"
          error={!!getFieldErrorProp('medicalExaminerName').error || false}
          verified={fieldVerified['medicalExaminerName']}
          verificationResult={
            verificationResult
              ? verificationResult['medicalExaminerName']
              : undefined
          }
          hideVerification={shouldHideVerificationByStatus(
            verificationStatus,
            'medicalExaminerName'
          )}
          onMouseEnter={() =>
            handleHoverField(fieldPolygon['medicalExaminerName'])
          }
          onMouseLeave={handleUnHoverField}
        />
      </FormControlWithStatus>
      <FormControlWithStatus
        id="ExaminersLicenseNumber"
        label="National Registry Number"
        disabled={disabled}
      >
        <InputWithStatus
          {...getFieldProps('examinerLicenseNumber')}
          id="ExaminersLicenseNumber"
          label="Examiner’s License Number"
          error={!!getFieldErrorProp('examinerLicenseNumber').error || false}
          verified={fieldVerified['examinerLicenseNumber']}
          verificationResult={
            verificationResult
              ? verificationResult['examinerLicenseNumber']
              : undefined
          }
          hideVerification={shouldHideVerificationByStatus(
            verificationStatus,
            'examinerLicenseNumber'
          )}
          onMouseEnter={() =>
            handleHoverField(fieldPolygon['examinerLicenseNumber'])
          }
          onMouseLeave={handleUnHoverField}
          onClickToAdornmentOrEnterKey={onVerify}
          canApprove={!canApprove}
        />
      </FormControlWithStatus>
      <FormControlWithStatus id="CertificateExpirationDate" disabled={disabled}>
        <DatePickerWithStatus
          label="Certificate Expiration Date"
          {...getFieldProps('expirationDate')}
          verified={fieldVerified['expirationDate']}
          verificationResult={
            verificationResult
              ? verificationResult['expirationDate']
              : undefined
          }
          disabled={disabled}
          hideVerification={shouldHideVerificationByStatus(
            verificationStatus,
            'expirationDate'
          )}
          onMouseEnter={() => handleHoverField(fieldPolygon['expirationDate'])}
          onMouseLeave={handleUnHoverField}
        />
      </FormControlWithStatus>
      <FormControlWithStatus
        id="DriversLicenseNumber"
        label="Driver License Number"
        disabled={disabled}
      >
        <InputWithStatus
          {...getFieldProps('driversLicenseNumber')}
          id="DriversLicenseNumber"
          label="Driver License Number"
          error={!!getFieldErrorProp('driversLicenseNumber').error || false}
          verified={fieldVerified['driversLicenseNumber']}
          verificationResult={
            verificationResult
              ? verificationResult['driversLicenseNumber']
              : undefined
          }
          hideVerification={shouldHideVerificationByStatus(
            verificationStatus,
            'driversLicenseNumber'
          )}
          onMouseEnter={() =>
            handleHoverField(fieldPolygon['driversLicenseNumber'])
          }
          onMouseLeave={handleUnHoverField}
        />
      </FormControlWithStatus>

      {featureFlags['med-card-check-box-fields'] && (
        <>
          <FormControlWithStatus id="issueDate" disabled={disabled}>
            <DatePickerWithStatus
              label="Issue Date *"
              {...getFieldProps('issueDate')}
              disabled={disabled}
              hideVerification={true}
              onMouseEnter={() => handleHoverField(fieldPolygon['issueDate'])}
              onMouseLeave={handleUnHoverField}
            />
          </FormControlWithStatus>

          <FormControlWithStatus
            id="medExaminerPhone"
            label="Medical examiner phone"
            disabled={disabled}
          >
            <InputWithStatus
              {...getFieldProps('medExaminerPhone')}
              id="medExaminerPhone"
              label="Medical examiner phone"
              error={!!getFieldErrorProp('medExaminerPhone').error || false}
              verified={fieldVerified['medExaminerPhone']}
              verificationResult={
                verificationResult
                  ? verificationResult['medExaminerPhone']
                  : undefined
              }
              hideVerification={shouldHideVerificationByStatus(
                verificationStatus,
                'medExaminerPhone'
              )}
              onMouseEnter={() =>
                handleHoverField(fieldPolygon['medExaminerPhone'])
              }
              onMouseLeave={handleUnHoverField}
            />
          </FormControlWithStatus>

          <div>Is this a return to work?</div>
          <FormControl disabled={true}>
            <RadioGroup
              row
              name={'isReturnToWork'}
              value={isReturnToWork ? 'yes' : 'no'}
            >
              <FormControlLabel
                value="yes"
                control={<Radio sx={{ '& svg': { fill: '#FFDB00' } }} />}
                label="Yes"
              />
              <FormControlLabel
                value="no"
                control={<Radio sx={{ '& svg': { fill: '#FFDB00' } }} />}
                label="No"
              />
            </RadioGroup>
          </FormControl>

          <div className={styles.checkBoxWrapper}>
            <Checkbox
              checked={
                getFieldProps('qualifiedInAccordanceWith49CFR39164').value
              }
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                getFieldProps('qualifiedInAccordanceWith49CFR39164').onChange(
                  event.target.checked
                );
                if (event.target.checked) {
                  getFieldProps(
                    'qualifiedInAccordanceWith49CFR39164WithApplicableStateVariances'
                  ).onChange(!event.target.checked);
                }
              }}
              disabled={disabled}
              sx={{
                '& .MuiSvgIcon-root': { fontSize: 21 },
                '&': { padding: '5px 5px 5px 0px' },
              }}
            />
            <div>Qualified in accordance with (49 CFR 391.41)</div>
          </div>

          <div className={styles.checkBoxWrapper}>
            <Checkbox
              checked={
                getFieldProps(
                  'qualifiedInAccordanceWith49CFR39164WithApplicableStateVariances'
                ).value
              }
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                getFieldProps(
                  'qualifiedInAccordanceWith49CFR39164WithApplicableStateVariances'
                ).onChange(event.target.checked);
                if (event.target.checked) {
                  getFieldProps('qualifiedInAccordanceWith49CFR39164').onChange(
                    !event.target.checked
                  );
                }
              }}
              disabled={disabled}
              sx={{
                '& .MuiSvgIcon-root': { fontSize: 21 },
                '&': { padding: '5px 5px 5px 0px' },
              }}
            />
            <div>
              Qualified in accordance with (49 CFR 391.41) with applicable State
              variances
            </div>
          </div>
          {(getFieldProps(
            'qualifiedInAccordanceWith49CFR39164WithApplicableStateVariances'
          ).value ||
            getFieldProps('qualifiedInAccordanceWith49CFR39164').value) && (
            <div style={{ paddingLeft: 16 }}>
              <div className={styles.checkBoxWrapper}>
                <Checkbox
                  checked={getFieldProps('wearingCorrectiveLenses').value}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    getFieldProps('wearingCorrectiveLenses').onChange(
                      event.target.checked
                    );
                  }}
                  disabled={disabled}
                  sx={{
                    '& .MuiSvgIcon-root': { fontSize: 21 },
                    '&': { padding: '5px 5px 5px 0px' },
                  }}
                />
                <div>Wearing corrective lenses</div>
              </div>

              <div className={styles.checkBoxWrapper}>
                <Checkbox
                  checked={getFieldProps('wearingHearingAid').value}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    getFieldProps('wearingHearingAid').onChange(
                      event.target.checked
                    );
                  }}
                  disabled={disabled}
                  sx={{
                    '& .MuiSvgIcon-root': { fontSize: 21 },
                    '&': { padding: '5px 5px 5px 0px' },
                  }}
                />
                <div>Wearing hearing aid</div>
              </div>

              <div className={styles.checkBoxWrapper}>
                <Checkbox
                  checked={
                    getFieldProps('accompaniedByWaiverOrExemption').value
                  }
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    getFieldProps('accompaniedByWaiverOrExemption').onChange(
                      event.target.checked
                    );
                  }}
                  disabled={disabled}
                  sx={{
                    '& .MuiSvgIcon-root': { fontSize: 21 },
                    '&': { padding: '5px 5px 5px 0px' },
                  }}
                />
                <div>Accompanied by waiver or exemption</div>
              </div>

              {getFieldProps('accompaniedByWaiverOrExemption').value && (
                <FormControlWithStatus
                  id="accompaniedByText"
                  label="Accompanied by text"
                  disabled={disabled}
                >
                  <InputWithStatus
                    {...getFieldProps('accompaniedByText')}
                    id="accompaniedByText"
                    label="Accompanied by text"
                    error={
                      !!getFieldErrorProp('accompaniedByText').error || false
                    }
                    verified={fieldVerified['accompaniedByText']}
                    verificationResult={
                      verificationResult
                        ? verificationResult['accompaniedByText']
                        : undefined
                    }
                    hideVerification={shouldHideVerificationByStatus(
                      verificationStatus,
                      'accompaniedByText'
                    )}
                    onMouseEnter={() =>
                      handleHoverField(fieldPolygon['accompaniedByText'])
                    }
                    onMouseLeave={handleUnHoverField}
                  />
                </FormControlWithStatus>
              )}

              <div className={styles.checkBoxWrapper}>
                <Checkbox
                  checked={getFieldProps('accompaniedSPESertificate').value}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    getFieldProps('accompaniedSPESertificate').onChange(
                      event.target.checked
                    );
                  }}
                  disabled={disabled}
                  sx={{
                    '& .MuiSvgIcon-root': { fontSize: 21 },
                    '&': { padding: '5px 5px 5px 0px' },
                  }}
                />
                <div>Accompanied S P E sertificate</div>
              </div>

              <div className={styles.checkBoxWrapper}>
                <Checkbox
                  checked={getFieldProps('drivingWithExempIntracityZone').value}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    getFieldProps('drivingWithExempIntracityZone').onChange(
                      event.target.checked
                    );
                  }}
                  disabled={disabled}
                  sx={{
                    '& .MuiSvgIcon-root': { fontSize: 21 },
                    '&': { padding: '5px 5px 5px 0px' },
                  }}
                />
                <div>Driving with exemp intracity zone</div>
              </div>

              <div className={styles.checkBoxWrapper}>
                <Checkbox
                  checked={getFieldProps('grandfatherdStateRequirements').value}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    getFieldProps('grandfatherdStateRequirements').onChange(
                      event.target.checked
                    );
                  }}
                  disabled={disabled}
                  sx={{
                    '& .MuiSvgIcon-root': { fontSize: 21 },
                    '&': { padding: '5px 5px 5px 0px' },
                  }}
                />
                <div>Grandfatherd state requirements</div>
              </div>

              <div className={styles.checkBoxWrapper}>
                <Checkbox
                  checked={getFieldProps('qualified49CFR39163').value}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    getFieldProps('qualified49CFR39163').onChange(
                      event.target.checked
                    );
                  }}
                  disabled={disabled}
                  sx={{
                    '& .MuiSvgIcon-root': { fontSize: 21 },
                    '&': { padding: '5px 5px 5px 0px' },
                  }}
                />
                <div>Qualified by operation of 49 CFR 391.64 (Federal)</div>
              </div>
            </div>
          )}
        </>
      )}
    </form>
  );
};
